import { HtmlTags } from "@components/common/html-tags";
import { Icon, Spinner } from "@components/common/icon";
import { PaginatorCursor } from "@components/common/paginator-cursor";
import { CommunitiesSortDropdown } from "@components/common/sort-dropdown";
import { FirstLoadService } from "@services/FirstLoadService";
import {
  EMPTY_REQUEST,
  LOADING_REQUEST,
  RequestState,
} from "@services/HttpService";
import { HttpService } from "@services/HttpService";
import { I18NextService } from "@services/I18NextService";
import {
  browserAdminListCommunities,
  adminListCommunities,
  browserListCommunityCategories,
  browserCreateCommunityCategory,
  browserDeleteCommunityCategory,
  browserEditCommunityCategory,
  CommunityCategory,
  listCommunityCategories,
  ListCommunityCategoriesResponse,
} from "@utils/admin-api";
import { isBrowser } from "@utils/browser";
import { communityLimit } from "@utils/config";
import { getHttpBaseInternal } from "@utils/env";
import { aimooT } from "@utils/aimoo-i18n";
import { editCommunity, setIsoData, toast } from "@utils/app";
import {
  getQueryParams,
  getQueryString,
  numToSI,
  resourcesSettled,
} from "@utils/helpers";
import { IRoutePropsWithFetch } from "@utils/routes";
import {
  InitialFetchRequest,
  QueryParams,
  RouteDataResponse,
} from "@utils/types";
import { Component, createRef, FormEvent, InfernoNode, linkEvent } from "inferno";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import {
  Community,
  CommunitySortType,
  CommunityView,
  PagedResponse,
  PaginationCursor,
} from "lemmy-js-client";
import { communityLink, CommunityLink } from "./community-link";
import { scrollMixin } from "../mixins/scroll-mixin";
import Tabs from "../common/tabs";
import classNames from "classnames";

interface AdminCommunitiesProps {
  sort: CommunitySortType;
  cursor?: PaginationCursor;
  search_term?: string;
}

type AdminCommunitiesData = RouteDataResponse<{
  listCommunitiesResponse: PagedResponse<CommunityView>;
  listCategoriesResponse: ListCommunityCategoriesResponse;
}>;

interface AdminCommunitiesState {
  listCommunitiesResponse: RequestState<PagedResponse<CommunityView>>;
  listCategoriesResponse: RequestState<ListCommunityCategoriesResponse>;
  categories: CommunityCategory[];
  savingCommunityScores: Set<number>;
  categoryName: string;
  categoryDisplayOrder: string;
  editingCategoryId?: number;
  categorySaving: boolean;
  isIsomorphic: boolean;
}

type AdminCommunitiesRouteProps = RouteComponentProps<Record<string, never>> &
  AdminCommunitiesProps;

export type AdminCommunitiesFetchConfig = IRoutePropsWithFetch<
  AdminCommunitiesData,
  Record<string, never>,
  AdminCommunitiesProps
>;

function getSortTypeFromQuery(type?: string): CommunitySortType {
  return type ? (type as CommunitySortType) : "new";
}

export function getAdminCommunitiesQueryParams(
  source?: string,
): AdminCommunitiesProps {
  return getQueryParams<AdminCommunitiesProps>(
    {
      sort: getSortTypeFromQuery,
      cursor: (cursor?: string) => cursor,
      search_term: (searchTerm?: string) => searchTerm,
    },
    source,
  );
}

@scrollMixin
export class AdminCommunities extends Component<
  AdminCommunitiesRouteProps,
  AdminCommunitiesState
> {
  private isoData = setIsoData<AdminCommunitiesData>(this.context);
  searchInput = createRef<HTMLInputElement>();

  state: AdminCommunitiesState = {
    listCommunitiesResponse: EMPTY_REQUEST,
    listCategoriesResponse: EMPTY_REQUEST,
    categories: [],
    savingCommunityScores: new Set(),
    categoryName: "",
    categoryDisplayOrder: "",
    categorySaving: false,
    isIsomorphic: false,
  };

  loadingSettled() {
    return resourcesSettled([this.state.listCommunitiesResponse]);
  }

  constructor(props: AdminCommunitiesRouteProps, context: object) {
    super(props, context);
    this.handleEditCategory = this.handleEditCategory.bind(this);
    this.handleCancelEditCategory = this.handleCancelEditCategory.bind(this);
    this.handleDeleteCategory = this.handleDeleteCategory.bind(this);

    if (FirstLoadService.isFirstLoad) {
      const { listCommunitiesResponse, listCategoriesResponse } =
        this.isoData.routeData;
      this.state = {
        ...this.state,
        listCommunitiesResponse,
        listCategoriesResponse,
        categories:
          listCategoriesResponse.state === "success"
            ? listCategoriesResponse.data.categories
            : [],
        isIsomorphic: true,
      };
    }
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      await this.refetch(this.props);
    }
  }

  async componentWillReceiveProps(nextProps: AdminCommunitiesRouteProps) {
    await this.refetch(nextProps);
  }

  get documentTitle(): string {
    return `${aimooT("admin_communities_title")} - ${this.isoData.siteRes.site_view.site.name}`;
  }

  render() {
    return (
      <div className="aimoo-admin-communities">
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />
        <div className="aimoo-admin-communities__inner container-lg px-3">
          <header className="aimoo-admin-communities__header">
            <div>
              <h1 className="aimoo-admin-communities__title">
                {aimooT("admin_communities_title")}
              </h1>
              <p className="aimoo-admin-communities__subtitle">
                {aimooT("admin_communities_subtitle")}
              </p>
            </div>
            <div className="aimoo-admin-communities__header-actions">
              <Link
                to="/create_community"
                className="btn btn-primary aimoo-admin-communities__create"
              >
                {aimooT("create_community_btn")}
              </Link>
            </div>
          </header>

          <div className="aimoo-admin-communities__card">
            <Tabs
              syncHash
              tabs={[
                {
                  key: "communities",
                  label: aimooT("admin_tab_list"),
                  getNode: isSelected => this.communitiesTab(isSelected),
                },
                {
                  key: "categories",
                  label: aimooT("admin_tab_categories"),
                  getNode: isSelected => this.categoriesTab(isSelected),
                },
              ]}
            />
          </div>
        </div>
      </div>
    );
  }

  communitiesTab(isSelected: boolean): InfernoNode {
    return (
      <div
        className={classNames("tab-pane show aimoo-admin-communities__tab", {
          active: isSelected,
        })}
        role="tabpanel"
        id="communities-tab-pane"
      >
        <div className="aimoo-admin-communities__toolbar">
          <form
            className="aimoo-admin-communities__search"
            onSubmit={e => handleSearchSubmit(this, e)}
          >
            <Icon
              icon="search"
              classes="aimoo-admin-communities__search-icon"
            />
            <input
              name="q"
              type="search"
              className="form-control"
              placeholder={aimooT("search_community")}
              aria-label={I18NextService.i18n.t("search")}
              defaultValue={this.props.search_term ?? ""}
              ref={this.searchInput}
            />
          </form>
          <div className="aimoo-admin-communities__sort">
            <CommunitiesSortDropdown
              currentOption={this.props.sort}
              onSelect={val => handleSortChange(this, val)}
              showLabel
            />
          </div>
        </div>

        <div className="aimoo-admin-communities__panel">
          {this.renderListingsTable()}
        </div>

        <PaginatorCursor
          current={this.props.cursor}
          resource={this.state.listCommunitiesResponse}
          onPageChange={cursor => handlePageChange(this, cursor)}
        />
      </div>
    );
  }

  categoriesTab(isSelected: boolean): InfernoNode {
    const {
      listCategoriesResponse,
      categories,
      categoryName,
      categoryDisplayOrder,
      editingCategoryId,
      categorySaving,
    } = this.state;

    return (
      <div
        className={classNames(
          "tab-pane show aimoo-admin-communities__tab aimoo-admin-categories",
          { active: isSelected },
        )}
        role="tabpanel"
        id="categories-tab-pane"
      >
        <p className="aimoo-admin-communities__tab-intro">
          {aimooT("admin_categories_intro")}
        </p>

        <form
          className="aimoo-admin-categories__form"
          onSubmit={linkEvent(this, handleCategorySubmit)}
        >
          <div className="aimoo-admin-categories__form-row">
            <div className="aimoo-create-community__field aimoo-admin-categories__field-name">
              <label
                className="aimoo-create-community__label"
                htmlFor="aimoo-admin-category-name"
              >
                {editingCategoryId
                  ? aimooT("edit_category_name")
                  : aimooT("add_category_name")}
                <span className="aimoo-create-community__required">*</span>
              </label>
              <input
                id="aimoo-admin-category-name"
                className="form-control aimoo-create-community__input"
                value={categoryName}
                maxLength={50}
                required
                disabled={categorySaving}
                onInput={linkEvent(this, handleCategoryNameChange)}
                placeholder={aimooT("category_name_placeholder")}
              />
            </div>
            <div className="aimoo-create-community__field aimoo-admin-categories__field-order">
              <label
                className="aimoo-create-community__label"
                htmlFor="aimoo-admin-category-order"
              >
                {aimooT("sort_order")}
              </label>
              <input
                id="aimoo-admin-category-order"
                className="form-control aimoo-create-community__input"
                type="number"
                value={categoryDisplayOrder}
                disabled={categorySaving}
                onInput={linkEvent(this, handleCategoryOrderChange)}
                placeholder={aimooT("sort_auto")}
              />
            </div>
          </div>
          <div className="aimoo-create-community__actions">
            <button
              type="submit"
              className="btn btn-primary aimoo-create-community__submit"
              disabled={categorySaving || !categoryName.trim()}
            >
              {categorySaving ? (
                <Spinner />
              ) : editingCategoryId ? (
                aimooT("save")
              ) : (
                aimooT("add")
              )}
            </button>
            {editingCategoryId && (
              <button
                type="button"
                className="btn aimoo-create-community__cancel"
                disabled={categorySaving}
                onClick={this.handleCancelEditCategory}
              >
                {aimooT("cancel")}
              </button>
            )}
          </div>
        </form>

        <div className="aimoo-admin-categories__list-wrap">
          <h2 className="aimoo-admin-categories__list-title">
            {aimooT("existing_categories")}
          </h2>
          {listCategoriesResponse.state === "loading" &&
            categories.length === 0 && (
            <div className="aimoo-admin-categories__loading">
              <Spinner large />
            </div>
          )}
          {listCategoriesResponse.state !== "loading" &&
            categories.length === 0 && (
              <div className="aimoo-admin-categories__empty">
                {aimooT("no_categories")}
              </div>
            )}
          {categories.length > 0 && (
            <div className="aimoo-admin-categories__list">
              {categories.map(category => (
                <div key={category.id} className="aimoo-admin-categories__row">
                  <div>
                    <div className="aimoo-admin-categories__name">
                      {category.name}
                    </div>
                    <div className="aimoo-admin-categories__meta">
                      {aimooT("category_meta", {
                        order: category.display_order,
                        id: category.id,
                      })}
                    </div>
                  </div>
                  <div className="aimoo-admin-categories__actions">
                    <button
                      type="button"
                      className="btn btn-sm aimoo-admin-categories__btn"
                      onClick={() => this.handleEditCategory(category)}
                    >
                      {aimooT("edit")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger aimoo-admin-categories__btn-danger"
                      onClick={() => this.handleDeleteCategory(category)}
                    >
                      {aimooT("delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  handleEditCategory(category: CommunityCategory) {
    this.setState({
      editingCategoryId: category.id,
      categoryName: category.name,
      categoryDisplayOrder: String(category.display_order),
    });
  }

  handleCancelEditCategory() {
    this.setState({
      editingCategoryId: undefined,
      categoryName: "",
      categoryDisplayOrder: "",
    });
  }

  applyCategories(categories: CommunityCategory[]) {
    this.setState({
      categories,
      listCategoriesResponse: {
        state: "success",
        data: { categories },
      },
    });
  }

  async handleDeleteCategory(category: CommunityCategory) {
    if (
      !window.confirm(
        aimooT("confirm_delete_category", { name: category.name }),
      )
    ) {
      return;
    }
    this.setState({ categorySaving: true });
    const res = await browserDeleteCommunityCategory({ id: category.id });
    this.setState({ categorySaving: false });
    if (res.state === "success") {
      toast(aimooT("deleted"));
      if (this.state.editingCategoryId === category.id) {
        this.handleCancelEditCategory();
      }
      // Update list immediately so the UI doesn't wait on a full refetch.
      this.applyCategories(
        this.state.categories.filter(c => c.id !== category.id),
      );
      await this.refetchCategories();
    } else {
      toast(res.err.message || aimooT("delete_failed"), "danger");
    }
  }

  async refetchCategories() {
    const listCategoriesResponse = await browserListCommunityCategories();
    if (listCategoriesResponse.state === "success") {
      this.applyCategories(listCategoriesResponse.data.categories);
    } else {
      this.setState({ listCategoriesResponse });
    }
  }

  renderListingsTable(): InfernoNode | void {
    switch (this.state.listCommunitiesResponse.state) {
      case "loading":
        return (
          <div className="aimoo-admin-communities__loading">
            <Spinner large />
          </div>
        );
      case "failed":
        return (
          <div className="alert alert-danger mb-0">
            {aimooT("admin_list_load_failed")}
          </div>
        );
      case "success": {
        const communities = this.state.listCommunitiesResponse.data.items;

        if (communities.length === 0) {
          return (
            <div className="aimoo-admin-communities__empty">
              {I18NextService.i18n.t("none_found")}
            </div>
          );
        }

        return (
          <div className="aimoo-admin-communities__table" id="admin-community-table">
            <div className="aimoo-admin-communities__thead d-none d-lg-grid">
              <div>{I18NextService.i18n.t("name")}</div>
              <div>{aimooT("category")}</div>
              <div>{aimooT("status")}</div>
              <div>{aimooT("official")}</div>
              <div>{aimooT("heat_index")}</div>
              <div>{aimooT("recommend_index")}</div>
              <div>{I18NextService.i18n.t("users")}</div>
              <div>{I18NextService.i18n.t("posts")}</div>
              <div>{I18NextService.i18n.t("comments")}</div>
              <div className="text-end">{aimooT("actions")}</div>
            </div>
            {communities.map(cv => this.renderCommunityRow(cv))}
          </div>
        );
      }
    }
  }

  renderCommunityRow(cv: CommunityView): InfernoNode {
    const id = cv.community.id as any;
    const saving = this.state.savingCommunityScores.has(id);
    const categoryId = (cv.community as Community & { category_id?: number })
      .category_id;
    const categoryName =
      this.state.categories.find(c => c.id === categoryId)?.name ??
      aimooT("uncategorized");

    return (
      <div key={cv.community.id} className="aimoo-admin-communities__row">
        <div className="aimoo-admin-communities__cell aimoo-admin-communities__cell--name">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {I18NextService.i18n.t("name")}
          </span>
          <CommunityLink
            community={cv.community}
            myUserInfo={this.isoData.myUserInfo}
            muted={cv.community.removed || cv.community.deleted}
          />
          <div className="aimoo-admin-communities__actor">
            !{cv.community.name}
          </div>
        </div>
        <div className="aimoo-admin-communities__cell">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {aimooT("category")}
          </span>
          <span
            className={`aimoo-admin-communities__badge${
              categoryId ? "" : " aimoo-admin-communities__badge--muted"
            }`}
          >
            {categoryName}
          </span>
        </div>
        <div className="aimoo-admin-communities__cell">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {aimooT("status")}
          </span>
          {communityStatus(cv.community)}
        </div>
        <div className="aimoo-admin-communities__cell aimoo-admin-communities__cell--official">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {aimooT("official")}
          </span>
          <label className="aimoo-admin-communities__official">
            <input
              type="checkbox"
              className="form-check-input aimoo-admin-communities__official-input"
              id={`is-official-${cv.community.id}`}
              defaultChecked={!!(cv.community as any).is_official}
              disabled={saving}
            />
            <span>{aimooT("official_community")}</span>
          </label>
        </div>
        <div className="aimoo-admin-communities__cell">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {aimooT("heat_index")}
          </span>
          <input
            type="number"
            className="form-control form-control-sm aimoo-admin-communities__score"
            min="0"
            step="0.1"
            defaultValue={(cv.community as any).hot_score_admin ?? 0}
            id={`hot-score-${cv.community.id}`}
            disabled={saving}
          />
        </div>
        <div className="aimoo-admin-communities__cell">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {aimooT("recommend_index")}
          </span>
          <input
            type="number"
            className="form-control form-control-sm aimoo-admin-communities__score"
            min="0"
            step="0.1"
            defaultValue={(cv.community as any).suggested_score ?? 0}
            id={`suggested-score-${cv.community.id}`}
            disabled={saving}
          />
        </div>
        <div className="aimoo-admin-communities__cell aimoo-admin-communities__cell--stat">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {I18NextService.i18n.t("users")}
          </span>
          {numToSI(cv.community.subscribers)}
        </div>
        <div className="aimoo-admin-communities__cell aimoo-admin-communities__cell--stat">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {I18NextService.i18n.t("posts")}
          </span>
          {numToSI(cv.community.posts)}
        </div>
        <div className="aimoo-admin-communities__cell aimoo-admin-communities__cell--stat">
          <span className="aimoo-admin-communities__cell-label d-lg-none">
            {I18NextService.i18n.t("comments")}
          </span>
          {numToSI(cv.community.comments)}
        </div>
        <div className="aimoo-admin-communities__cell aimoo-admin-communities__cell--actions">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={saving}
            onClick={() => handleSaveCommunityScores(this, id)}
          >
            {saving ? <Spinner /> : aimooT("save")}
          </button>
          <Link
            to={communityLink(cv.community).link}
            className="btn btn-sm aimoo-admin-communities__btn-secondary"
          >
            {aimooT("view")}
          </Link>
          <Link
            to={`${communityLink(cv.community).link}/settings`}
            className="btn btn-sm aimoo-admin-communities__btn-secondary"
          >
            {aimooT("settings")}
          </Link>
        </div>
      </div>
    );
  }

  updateUrl(props: Partial<AdminCommunitiesProps>) {
    const { sort, cursor, search_term } = { ...this.props, ...props };
    const queryParams: QueryParams<AdminCommunitiesProps> = {
      sort,
      cursor,
      search_term,
    };

    this.props.history.push(`/admin/communities${getQueryString(queryParams)}`);
  }

  static fetchInitialData = async ({
    headers,
    query: { sort, cursor, search_term },
  }: InitialFetchRequest<
    Record<string, never>,
    AdminCommunitiesProps
  >): Promise<AdminCommunitiesData> => ({
    listCommunitiesResponse: await adminListCommunities(
      getHttpBaseInternal(),
      {
        sort,
        page_cursor: cursor,
        search_term,
        limit: communityLimit,
      },
      headers,
    ),
    listCategoriesResponse: await listCommunityCategories(
      getHttpBaseInternal(),
      headers,
    ),
  });

  async refetch({ sort, cursor, search_term }: AdminCommunitiesProps) {
    const needCategories =
      this.state.listCategoriesResponse.state !== "success";
    this.setState({
      listCommunitiesResponse: LOADING_REQUEST,
      ...(needCategories
        ? { listCategoriesResponse: LOADING_REQUEST }
        : {}),
    });
    const [listCommunitiesResponse, listCategoriesResponse] = await Promise.all(
      [
        browserAdminListCommunities({
          sort,
          page_cursor: cursor,
          search_term,
          limit: communityLimit,
        }),
        needCategories
          ? browserListCommunityCategories()
          : Promise.resolve(this.state.listCategoriesResponse),
      ],
    );

    const nextState: Partial<AdminCommunitiesState> = {
      listCommunitiesResponse,
      listCategoriesResponse,
    };
    if (listCategoriesResponse.state === "success") {
      nextState.categories = listCategoriesResponse.data.categories;
    }
    this.setState(nextState);
  }
}

function communityStatus(community: Community) {
  return (
    <div className="aimoo-admin-communities__badges">
      {community.removed && (
        <span className="aimoo-admin-communities__badge aimoo-admin-communities__badge--danger">
          {aimooT("removed")}
        </span>
      )}
      {community.deleted && (
        <span className="aimoo-admin-communities__badge aimoo-admin-communities__badge--muted">
          {aimooT("deleted_status")}
        </span>
      )}
      {community.nsfw && (
        <span className="aimoo-admin-communities__badge aimoo-admin-communities__badge--warn">
          NSFW
        </span>
      )}
      <span className="aimoo-admin-communities__badge">
        {community.local ? aimooT("local") : aimooT("remote")}
      </span>
    </div>
  );
}

function handleSortChange(i: AdminCommunities, sort: CommunitySortType) {
  i.updateUrl({ sort, cursor: undefined });
}

function handleSearchSubmit(
  i: AdminCommunities,
  event: FormEvent<HTMLFormElement>,
) {
  event.preventDefault();
  i.updateUrl({ search_term: i.searchInput.current?.value, cursor: undefined });
}

function handlePageChange(i: AdminCommunities, cursor?: PaginationCursor) {
  i.updateUrl({ cursor });
}

async function handleSaveCommunityScores(
  i: AdminCommunities,
  communityId: number,
) {
  const savingSet = new Set(i.state.savingCommunityScores);
  savingSet.add(communityId);
  i.setState({ savingCommunityScores: savingSet });

  const hotScoreInput = document.getElementById(
    `hot-score-${communityId}`,
  ) as HTMLInputElement | null;
  const suggestedScoreInput = document.getElementById(
    `suggested-score-${communityId}`,
  ) as HTMLInputElement | null;
  const officialInput = document.getElementById(
    `is-official-${communityId}`,
  ) as HTMLInputElement | null;

  const hot_score_admin = hotScoreInput
    ? parseFloat(hotScoreInput.value) || 0
    : 0;
  const suggested_score = suggestedScoreInput
    ? parseFloat(suggestedScoreInput.value) || 0
    : 0;
  const is_official = officialInput?.checked ?? false;

  const form = {
    community_id: communityId as any,
    hot_score_admin,
    suggested_score,
    is_official,
  };

  try {
    const res = await HttpService.client.editCommunity(form as any);

    if (res.state === "success") {
      if (i.state.listCommunitiesResponse.state === "success") {
        const updatedCommunities = editCommunity(
          res.data.community_view,
          i.state.listCommunitiesResponse.data.items,
        );
        i.setState({
          listCommunitiesResponse: {
            ...i.state.listCommunitiesResponse,
            data: {
              ...i.state.listCommunitiesResponse.data,
              items: updatedCommunities,
            },
          },
        });
      }

      toast(I18NextService.i18n.t("saved"));
    } else if (res.state === "failed") {
      toast(res.err.message ?? aimooT("save_failed"), "danger");
    }
  } catch (e: any) {
    toast(e?.message ?? aimooT("save_failed"), "danger");
  }

  const newSavingSet = new Set(i.state.savingCommunityScores);
  newSavingSet.delete(communityId);
  i.setState({ savingCommunityScores: newSavingSet });
}

function handleCategoryNameChange(
  i: AdminCommunities,
  event: FormEvent<HTMLInputElement>,
) {
  i.setState({ categoryName: event.currentTarget.value });
}

function handleCategoryOrderChange(
  i: AdminCommunities,
  event: FormEvent<HTMLInputElement>,
) {
  i.setState({ categoryDisplayOrder: event.currentTarget.value });
}

async function handleCategorySubmit(
  i: AdminCommunities,
  event: FormEvent<HTMLFormElement>,
) {
  event.preventDefault();
  const name = i.state.categoryName.trim();
  if (!name) {
    return;
  }

  const displayOrderRaw = i.state.categoryDisplayOrder.trim();
  const display_order =
    displayOrderRaw === "" ? undefined : Number(displayOrderRaw);
  if (
    display_order !== undefined &&
    (!Number.isFinite(display_order) || !Number.isInteger(display_order))
  ) {
    toast(aimooT("sort_must_integer"), "danger");
    return;
  }

  i.setState({ categorySaving: true });
  const res = i.state.editingCategoryId
    ? await browserEditCommunityCategory({
        id: i.state.editingCategoryId,
        name,
        display_order,
      })
    : await browserCreateCommunityCategory({ name, display_order });
  i.setState({ categorySaving: false });

  if (res.state === "success") {
    toast(
      i.state.editingCategoryId ? aimooT("saved") : aimooT("added"),
    );
    i.setState({
      editingCategoryId: undefined,
      categoryName: "",
      categoryDisplayOrder: "",
    });
    await i.refetchCategories();
  } else {
    toast(res.err.message || aimooT("operation_failed"), "danger");
  }
}
